/**
 * People (owner) / Team (menejer, team lead): xodimlar ro'yxati davr natijalari bilan -- kim nechta yangi, yuborgan,
 * ishga olgan, qo'ng'iroq qilgan, oxirgi faolligi, kechikkan vazifalari. Qator -- xodim sahifasiga. Boshqaruv
 * (qo'shish, bloklash, ruxsatlar) -- Admin -> Users da qoladi; bu sahifa natijalar uchun (redesign v2).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import { cn, formatWhen } from "@/lib/utils";
import { findItems } from "@/lib/textsearch";
import { openInNewTab } from "@/lib/clipboard";
import { scopedBranch, useBranchFilter } from "@/app/branchFilter";
import { useMe } from "@/features/auth/hooks";
import { can, type Me, type Permission, type Role } from "@/features/auth/types";
import { currentPeriod } from "@/features/dashboards/api";
import { PeriodSelect, pct } from "@/features/dashboards/blocks";
import { recruiterRowMenu } from "@/features/dashboards/menu";
import { usePeople, type PersonRow } from "@/features/people/api";
import { usePersonNav } from "@/features/people/usePersonNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox, FilterSelect, Input } from "@/components/ui/field";
import { Avatar, EmptyState, ErrorState, PageHeader, PageLoading, SimilarNote } from "@/components/ui/misc";
import { FilterChips, Table, Td, Th, Tr } from "@/components/ui/table";
import { ContextMenu } from "@/components/ui/context-menu";
import { ScopePicker } from "@/components/ScopePicker";
import { PersonLink } from "@/components/PersonLink";

type RoleChip = "all" | Exclude<Role, "carrier">;
// Faollik filtri: N kundan beri jim yoki hech qachon faol bo'lmagan (backend metrics.is_quiet; bosh sahifa "jim recruiterlar" -- 2)
const QUIET_OPTIONS = ["2", "7", "30"];

export function PeoplePage() {
  const me = useMe();
  if (!me.data) return <PageLoading />;
  return <People me={me.data} />;
}

function People({ me }: { me: Me }) {
  const { t } = useTranslation("people");
  const { t: tc } = useTranslation();
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const personNav = usePersonNav();
  const [params, setParams] = useSearchParams();
  const { branchId } = useBranchFilter();
  const [q, setQ] = useState("");
  const period = params.get("period") ?? currentPeriod();
  const role = (params.get("role") as RoleChip | null) ?? "all";
  const showBlocked = params.get("blocked") === "1";
  const quiet = params.get("quiet") ?? "";
  const hasFilters = role !== "all" || !!quiet || showBlocked;
  const isOwner = me.role === "owner";
  const manage = can(me, "users");

  const set = (key: string, value: string | null) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });

  const clearFilters = () =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      for (const key of ["role", "quiet", "blocked"]) next.delete(key);
      return next;
    });

  const people = usePeople({ period, branch: scopedBranch(me, branchId), role: role === "all" ? undefined : role, blocked: showBlocked ? 1 : undefined, quiet: quiet || undefined });
  // Ism yoki login bo'yicha (lib/textsearch: tutuq belgilari, kirill, xato yozilganda o'xshashlari)
  const found = useMemo(() => findItems(people.data?.results ?? [], q, (r) => `${r.full_name} ${r.username}`), [people.data, q]);
  const rows = found.items;

  const paths = { navigate: (p: string) => navigate(p), openNewTab: openInNewTab, profile: personNav, can: (perm: Permission) => can(me, perm) };
  const chips: { key: RoleChip; label: string }[] = [
    { key: "all", label: t("list.roles.all") },
    { key: "recruiter", label: t("list.roles.recruiter") },
    { key: "branch_manager", label: t("list.roles.branch_manager") },
  ];

  return (
    <div>
      <PageHeader
        title={isOwner ? t("list.title") : t("list.team")}
        subtitle={people.data ? t("list.subtitle", { count: people.data.results.length, period: people.data.period.label }) : undefined}
        actions={
          <>
            <ScopePicker me={me} />
            <PeriodSelect value={period} onChange={(v) => set("period", v === currentPeriod() ? null : v)} />
            {manage && (
              <Link to="/admin/users?new=1">
                <Button variant="primary">
                  <Plus size={14} strokeWidth={2.25} aria-hidden /> {t("list.newUser")}
                </Button>
              </Link>
            )}
          </>
        }
      >
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <FilterChips<RoleChip> items={chips} value={role} onChange={(k) => set("role", k === "all" ? null : k)} />
          <FilterSelect value={quiet} onChange={(e) => set("quiet", e.target.value || null)} className="w-44" aria-label={t("list.activity.label")}>
            <option value="">{t("list.activity.all")}</option>
            {/* URL'dan ro'yxatda yo'q kun soni kelsa ham tanlangan holda ko'rinadi */}
            {[...QUIET_OPTIONS, ...(quiet && quiet !== "never" && !QUIET_OPTIONS.includes(quiet) ? [quiet] : [])].map((d) => (
              <option key={d} value={d}>
                {t("list.activity.quiet", { count: Number(d) })}
              </option>
            ))}
            <option value="never">{t("list.activity.never")}</option>
          </FilterSelect>
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted" aria-hidden />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("list.search")} className="w-[240px] pl-8" aria-label={t("list.search")} />
          </div>
          {manage && <Checkbox label={t("list.showBlocked")} checked={showBlocked} onChange={(e) => set("blocked", e.target.checked ? "1" : null)} />}
          {hasFilters && (
            <Button variant="link" onClick={clearFilters}>
              {tc("actions.clearFilters")}
            </Button>
          )}
        </div>
      </PageHeader>

      {people.isPending ? (
        <PageLoading />
      ) : people.isError ? (
        <ErrorState message={people.error.message} onRetry={() => people.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={t("list.empty")} />
      ) : (
        <div className={cn(people.isFetching && "opacity-70 transition-opacity")}>
          <SimilarNote show={found.similar} />
          <Table>
            <thead>
              <tr>
                <Th>{t("list.cols.person")}</Th>
                <Th>{t("list.cols.scope")}</Th>
                <Th className="text-right">{t("list.cols.drivers")}</Th>
                <Th className="text-right">{t("list.cols.new")}</Th>
                <Th className="text-right">{t("list.cols.submitted")}</Th>
                <Th className="text-right">{t("list.cols.hired")}</Th>
                <Th className="text-right">{t("list.cols.conv")}</Th>
                <Th className="text-right">{t("list.cols.calls")}</Th>
                <Th>{t("list.cols.last")}</Th>
                <Th className="text-right">{t("list.cols.overdue")}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: PersonRow) => (
                <ContextMenu key={r.id} items={() => recruiterRowMenu({ user_id: r.id, display_name: r.display_name }, paths, tm)}>
                  <Tr clickable={personNav.can(r.id, r.role)} onClick={personNav.can(r.id, r.role) ? () => personNav.open(r.id) : undefined} className={cn(r.is_blocked && "opacity-60")}>
                    <Td>
                      <span className="flex items-center gap-3">
                        <Avatar initials={r.initials} name={r.full_name} size={34} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-2 text-[14px] font-semibold">
                            <PersonLink id={r.id} name={r.full_name} role={r.role} />
                            {r.role !== "recruiter" && <Badge tone="blue">{tc(`roles.${r.role}`)}</Badge>}
                            {r.is_blocked && <Badge tone="red">{t("list.blocked")}</Badge>}
                          </span>
                        </span>
                      </span>
                    </Td>
                    <Td className="text-muted">{r.branch?.name ?? "—"}</Td>
                    <Td className="tabular text-right">{r.drivers_active}</Td>
                    <Td className="tabular text-right">{r.new}</Td>
                    <Td className="tabular text-right">{r.submitted}</Td>
                    <Td className="tabular text-right font-semibold">{r.hired}</Td>
                    <Td className="tabular text-right text-muted">{pct(r.conversion)}</Td>
                    <Td className="tabular text-right">{r.calls}</Td>
                    <Td className={cn("whitespace-nowrap", r.last_activity_at ? "text-muted" : "text-faint")}>{r.last_activity_at ? formatWhen(r.last_activity_at) : t("list.never")}</Td>
                    <Td className={cn("tabular text-right", r.overdue_tasks > 0 ? "font-semibold text-danger" : "text-faint")}>{r.overdue_tasks}</Td>
                  </Tr>
                </ContextMenu>
              ))}
            </tbody>
          </Table>
          <p className="px-1 py-3 text-[12px] text-muted">{t("list.hint")}</p>
        </div>
      )}
    </div>
  );
}
